import { useCart } from "../cart/CartContext";
import { uploadCartTempFiles } from "../api/CartTempFiles";
import { createCartItemId } from "../cart/cartUtils";
import { exportGridImages } from "../utils/exportGridImages";
import { resizeImageFile } from "../utils/resizeImageFile";

async function dataUrlToFile(dataUrl, fileName) {
  const response = await fetch(dataUrl);
  const blob = await response.blob();

  return new File([blob], fileName, {
    type: blob.type || "image/png",
  });
}

export function useProcessOrder({
  grid,
  scale,
  offset,
  canvasRef,
  img,
  originalFile,
  loading,
  setLoading,
  setToast,
  setProgress,
}) {
  const { addItem } = useCart();

  const processOrder = async () => {
    if (loading) return;

    setLoading(true);
    setProgress({
      value: 5,
      message: "Preparando los recortes...",
    });

    try {
      const cartItemId = createCartItemId(grid.id);
      const generatedPieces = exportGridImages({
        canvas: canvasRef.current,
        img,
        grid,
        scale,
        offset,
        pieceSize: 600,
        quality: 0.9,
      });

      if (generatedPieces.length !== grid.cols * grid.rows) {
        throw new Error("No se pudieron generar todos los recortes.");
      }

      const pieceFiles = await Promise.all(
        generatedPieces.map((piece, index) =>
          dataUrlToFile(piece, `pieza-${index + 1}.jpg`)
        )
      );

      setProgress({
        value: 20,
        message: "Comprimiendo la imagen original...",
      });

      const compressedOriginal = await resizeImageFile(originalFile, {
        maxSize: 1200,
        quality: 0.8,
        fileName: "original.jpg",
      });

      setProgress({
        value: 40,
        message: "Subiendo las imagenes...",
      });

      const tempFiles = await uploadCartTempFiles({
        cartItemId,
        files: [
          compressedOriginal,
          ...pieceFiles,
        ],
      });

      setProgress({
        value: 90,
        message: "Guardando el collage en el carrito...",
      });

      addItem({
        cartItemId,
        productId: grid.id,
        productName: grid.name || grid.label,
        category: grid.category || "collage",
        type: grid.type || "collage",
        quantity: 1,
        unitPrice: grid.price || 0,
        currency: grid.currency || "EUR",
        options: {
          size: {
            label: "Formato",
            value: grid.id,
            valueLabel: grid.name || grid.label,
          },
          layout: {
            label: "Composicion",
            value: `${grid.cols}x${grid.rows}`,
            valueLabel: `${grid.cols} x ${grid.rows}`,
          },
          pieces: {
            label: "Piezas",
            value: String(grid.cols * grid.rows),
            valueLabel: `${grid.cols * grid.rows} piezas`,
          },
        },
        assets: [
          {
            kind: "uploaded_image",
            role: "collage_original",
            fileName: originalFile?.name || "original.jpg",
            includedInOrder: true,
          },
          ...generatedPieces.map((_, index) => ({
            kind: "generated_image",
            role: "collage_piece",
            fileName: `pieza-${index + 1}.jpg`,
            includedInOrder: true,
            width: 600,
            height: 600,
            mimeType: "image/jpeg",
          })),
        ],
        tempFiles,
        editorState: {
          scale,
          offset,
          canvasWidth: canvasRef.current?.width || null,
          canvasHeight: canvasRef.current?.height || null,
          cols: grid.cols,
          rows: grid.rows,
          pieceWidth: 600,
          pieceHeight: 600,
          pieceFormat: "image/jpeg",
        },
        notes: [
          "Imagen original subida temporalmente hasta crear el pedido.",
        ],
      });

      setProgress({
        value: 100,
        message: "Collage anadido correctamente.",
      });
      setToast("Collage anadido al carrito.");
    } catch (error) {
      setToast("No se pudo guardar la imagen del collage. Intentalo de nuevo.");
      setProgress({
        value: 0,
        message: "",
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    processOrder,
  };
}
